import { getSurveysOptions } from "@pulse/api/queryApi";
import usePulseId from "@pulse/hooks/usePulseId";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import {
    faBallotCheck,
    faPenToSquare,
    faSquareCheck,
    type IconDefinition,
} from "@fortawesome/sharp-regular-svg-icons";
import { faChartSimple } from "@fortawesome/pro-regular-svg-icons";
import { Card, CardContent, CardHeader, Skeleton } from "frontend-components";

function StatusBox({
    title,
    icon,
    accent,
    children,
}: {
    title: string;
    icon: IconDefinition;
    accent: string;
    children: ReactNode;
}) {
    return (
        <Card className="col-span-4 relative gap-1 shadow-sm sm:col-span-2">
            <div className={`absolute inset-x-0 top-0 h-1 ${accent}`} />
            <CardHeader className="px-4 text-sm relative z-20">{title}</CardHeader>
            <CardContent className="px-4 flex justify-between items-end">
                {children}

                <FontAwesomeIcon icon={icon} className="text-xl" />
            </CardContent>
        </Card>
    );
}

function ActiveSurveysBox() {
    const { t } = useTranslation();
    const pulseId = usePulseId();
    const { data, isLoading } = useQuery(getSurveysOptions({ pulseInstanceId: pulseId }));

    const count = useMemo(
        () => data?.filter((survey) => survey.status === "ACTIVE").length || 0,
        [data],
    );

    return (
        <StatusBox
            title={t("dashboard.activeSurveys")}
            icon={faBallotCheck}
            accent="bg-chameleon-pink"
        >
            <div className="font-light text-sm">
                {isLoading ? (
                    <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
                ) : (
                    <div className="text-2xl font-bold">{count}</div>
                )}
            </div>
        </StatusBox>
    );
}

function CompletedSurveysBox() {
    const { t } = useTranslation();
    const pulseId = usePulseId();
    const { data, isLoading } = useQuery(getSurveysOptions({ pulseInstanceId: pulseId }));

    const count = useMemo(
        () => data?.filter((survey) => survey.status === "COMPLETED").length || 0,
        [data],
    );
    return (
        <StatusBox
            title={t("dashboard.completedSurveys")}
            icon={faSquareCheck}
            accent="bg-chameleon-green"
        >
            <div className="font-light text-sm">
                {isLoading ? (
                    <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
                ) : (
                    <div className="text-2xl font-bold">{count}</div>
                )}
            </div>
        </StatusBox>
    );
}

function DraftsBox() {
    const { t } = useTranslation();
    const pulseId = usePulseId();
    const { data, isLoading } = useQuery(getSurveysOptions({ pulseInstanceId: pulseId }));

    const count = useMemo(
        () => data?.filter((survey) => survey.status === "DRAFT").length || 0,
        [data],
    );
    return (
        <StatusBox
            title={t("dashboard.draftSurveys")}
            icon={faPenToSquare}
            accent="bg-chameleon-yellow"
        >
            <div className="font-light text-sm">
                {isLoading ? (
                    <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
                ) : (
                    <div className="text-2xl font-bold">{count}</div>
                )}
            </div>
        </StatusBox>
    );
}

function TotalSurveysBox() {
    const { t } = useTranslation();
    const pulseId = usePulseId();
    const { data, isLoading } = useQuery(getSurveysOptions({ pulseInstanceId: pulseId }));

    return (
        <StatusBox
            title={t("dashboard.totalSurveys")}
            icon={faChartSimple}
            accent="bg-gradient-brand"
        >
            <div className="font-light text-sm">
                {isLoading ? (
                    <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
                ) : (
                    <div className="text-2xl font-bold">{data?.length || 0}</div>
                )}
            </div>
        </StatusBox>
    );
}

export { StatusBox, ActiveSurveysBox, CompletedSurveysBox, DraftsBox, TotalSurveysBox };
